import { HStack, Text, VStack } from '@chakra-ui/react';
import { Slider } from '@components/ui/slider';
import { Switch } from '@components/ui/switch';
import { useAppSelector } from '@hooks/redux.hooks';
import { selectActiveUser } from '@redux/authent/authent.selectors';
import { selectSettingsState } from '@redux/settings/settings.selectors';
import {
  usePatchAxisInvertedYMutation,
  usePatchLinearJoystickCoefficientMutation,
  usePatchZoomJoystickCameraSensitivityMutation,
} from '@services/config/setting.api';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

export default function CameraSettings() {
  const { login } = useAppSelector(selectActiveUser);
  const settings = useAppSelector(selectSettingsState);

  const [updateJoystickInversion] = usePatchAxisInvertedYMutation();
  const [updateLinearJoystickCoefficient] = usePatchLinearJoystickCoefficientMutation();
  const [updateZoomJoystickCameraSensitivity] = usePatchZoomJoystickCameraSensitivityMutation();

  const handleJoystickInversion = () => {
    updateJoystickInversion({ login: login, axisInvertedY: !settings.userSettings.axisInvertedY });
  };

  const handleLinearJoystickCoefficient = (value: number) => {
    updateLinearJoystickCoefficient({ login: login, linearJoystickCoefficient: value });
  };
  const handleZoomJoystickCameraSensitivity = (value: number) => {
    updateZoomJoystickCameraSensitivity({ login: login, zoomJoystickCameraSensitivity: value });
  };

  const linearJoystickCoefficient = useMemo(
    () => [Math.round(settings.userSettings.linearJoystickCoefficient * 100)],
    [settings.userSettings.linearJoystickCoefficient],
  );

  const zoomJoystickCameraSensitivity = useMemo(
    () => [Math.round(settings.userSettings.zoomJoystickCameraSensitivity * 100)],
    [settings.userSettings.zoomJoystickCameraSensitivity],
  );

  return (
    <VStack width="100%" backgroundColor="neutral.800" gap={4} padding={4} alignItems="start">
      <Text fontSize="22px">
        <FormattedMessage id="components.userSettings.camera.title" />
      </Text>
      <HStack gap={5} width="100%">
        <VStack gap={2} width="50%" alignItems="start" height="100%">
          <Text textStyle="md" color="neutral.300">
            <FormattedMessage id="components.userSettings.camera.linearJoystickCoefficient" />
          </Text>
          <VStack gap={1} width="100%" alignItems="start">
            <HStack justifyContent="space-between" width="100%">
              <Text textStyle="md" color="neutral.white">
                <FormattedMessage id="components.userSettings.camera.cubic" />
              </Text>
              <Text textStyle="md" color="neutral.white" marginRight="48px">
                <FormattedMessage id="components.userSettings.camera.linear" />
              </Text>
            </HStack>
            <HStack width="100%" gap={2}>
              <Slider
                width="100%"
                step={10}
                value={linearJoystickCoefficient}
                onValueChange={(value) => handleLinearJoystickCoefficient(value.value.at(0)! / 100)}
              />
              <Text color="neutral.300">{Math.round(settings.userSettings.linearJoystickCoefficient * 100)}%</Text>
            </HStack>
          </VStack>
          <HStack width="100%" gap={2}>
            <Switch checked={settings.userSettings.axisInvertedY} onCheckedChange={handleJoystickInversion} />
            <Text textStyle="md">
              <FormattedMessage id="components.userSettings.camera.axisInvertedY" />
            </Text>
          </HStack>
        </VStack>
        <VStack gap={2} width="50%" alignItems="start" height="100%">
          <Text textStyle="md" color="neutral.300">
            <FormattedMessage id="components.userSettings.camera.zoomJoystickCameraSensitivity" />
          </Text>
          <VStack gap={1} width="100%" alignItems="start">
            <HStack justifyContent="space-between" width="100%">
              <Text textStyle="md" color="neutral.white">
                <FormattedMessage id="components.userSettings.camera.noZoom" />
              </Text>
              <Text textStyle="md" color="neutral.white" marginRight="48px">
                <FormattedMessage id="components.userSettings.camera.zoom" />
              </Text>
            </HStack>
            <HStack width="100%" gap={2}>
              <Slider
                width="100%"
                step={10}
                value={zoomJoystickCameraSensitivity}
                onValueChange={(value) => handleZoomJoystickCameraSensitivity(value.value.at(0)! / 100)}
              />
              <Text color="neutral.300">{Math.round(settings.userSettings.zoomJoystickCameraSensitivity * 100)}%</Text>
            </HStack>
          </VStack>
        </VStack>
      </HStack>
    </VStack>
  );
}
